<template>
  <div>
    <container
      :isModal="true"
      @close-modal="closeScreen()"
      :loading="stateLoading || !stateReady"
    >
      <template v-slot:header>
        <h3 v-text="titulo" />
      </template>
      <template v-slot:body>
        <form @submit.prevent="submitSave" v-if="stateReady">
          <fieldset :disabled="stateBlocked">
            <div
              class="rntrc-aviso rntrc-aviso--info p-mb-3"
              v-if="avisoOrigem"
            >
              <i class="fas fa-info-circle p-mr-2" />
              {{ avisoOrigem }}
            </div>
            <div class="p-formgrid p-grid">
              <div class="p-field p-fluid p-col-12 p-md-5">
                <input-with-label label="CPF/CNPJ" :required="true" v-slot="l">
                  <InputText
                    :id="l.id"
                    v-model="entity.cpfCnpj"
                    :required="l.required"
                    :disabled="!!entity.idPessoaAntt"
                    :class="{ 'p-invalid': cpfCnpjInvalido }"
                    v-maska="['###.###.###-##', '##.###.###/####-##']"
                    maxlength="18"
                    v-focus
                    @blur="preencher()"
                  />
                </input-with-label>
                <small class="p-error" v-if="cpfCnpjInvalido"
                  >CPF/CNPJ inválido</small
                >
              </div>
              <div class="p-field p-fluid p-col-12 p-md-7">
                <input-with-label
                  label="Nome ou razão social"
                  :required="true"
                  v-slot="l"
                >
                  <InputText
                    :id="l.id"
                    v-model="entity.nome"
                    :required="l.required"
                    maxlength="250"
                  />
                </input-with-label>
              </div>
            </div>
            <button-group>
              <Button
                type="submit"
                class="p-button-rounded"
                :label="entity.idPessoaAntt ? 'Salvar alterações' : 'Cadastrar'"
                :loading="stateLoading"
              />
              <Button
                type="button"
                label="Cancelar"
                class="p-button-rounded p-button-text"
                @click="closeScreen()"
              />
            </button-group>
          </fieldset>
        </form>
      </template>
    </container>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  reactive,
  toRefs,
  computed,
  onMounted,
  PropType,
  Ref
} from "vue";
import { useAuth } from "../../../modules/auth";
import { useToast } from "../../../modules/message";
import { ClientePayload } from "../../../modules/business/cliente/clienteBusiness";
import {
  PessoaAnttPayload,
  safePessoaAntt,
  usePessoaAnttService
} from "../../../modules/business/rntrc/pessoa/pessoaAnttBusiness";
import {
  formatCpfCnpj,
  isCpfCnpjValido,
  somenteDigitos
} from "../../../modules/business/rntrc/rntrcTypes";

interface CadastroPessoaAnttState {
  entity: PessoaAnttPayload;
  stateReady: boolean;
  savedEntity: boolean;
  stateId: string;
  loadings: Array<Ref<boolean>>;
  /** Último documento consultado no preenchimento (evita repetir a busca). */
  documentoPreenchido: string;
}

/**
 * Cadastro simplificado de pessoa da ANTT (transportador, proprietário, arrendatário): só
 * CPF/CNPJ e nome. Ao informar o documento, traz a pessoa já cadastrada na ANTT ou o nome que a
 * GovPass tem dele; grava só no cadastro da ANTT.
 */
export default defineComponent({
  setup(props, { emit }) {
    const state = reactive<CadastroPessoaAnttState>({
      entity: safePessoaAntt(),
      stateReady: false,
      savedEntity: false,
      stateId:
        "state-" +
        Math.random()
          .toString(36)
          .substring(2),
      loadings: [],
      documentoPreenchido: ""
    });

    const stateLoading = computed(
      () => state.loadings.length !== 0 || !!props.parentLoading
    );

    const stateBlocked = computed(
      () =>
        stateLoading.value ||
        (!!state.entity.permissionInfo &&
          !useAuth().amIAllowed("edit", state.entity.permissionInfo))
    );

    const titulo = computed(
      () =>
        (state.entity.idPessoaAntt ? "Alterar " : "Novo ") +
        (props.papel || "transportador").toLowerCase()
    );

    const cpfCnpjInvalido = computed(
      () =>
        somenteDigitos(state.entity.cpfCnpj).length >= 11 &&
        !isCpfCnpjValido(state.entity.cpfCnpj)
    );

    const avisoOrigem = computed(() => {
      switch (state.entity.origem) {
        case "ANT":
          return "Este CPF/CNPJ já está no cadastro da ANTT: ao salvar, ele é atualizado.";
        case "GOV":
          return "Nome trazido do cadastro de transportadores da GovPass, que não é alterado. Confira e salve.";
        default:
          return "";
      }
    });

    const closeScreen = () => {
      emit(
        "after-register-screen-closed",
        state.savedEntity ? state.entity : undefined
      );
    };

    const removeLoading = (loading: Ref<boolean>) => {
      const idx = state.loadings.indexOf(loading);
      if (idx >= 0) {
        state.loadings.splice(idx, 1);
      }
    };

    const setEntity = (entity?: PessoaAnttPayload | null) => {
      state.entity = safePessoaAntt(entity);
      state.entity.cpfCnpj = formatCpfCnpj(state.entity.cpfCnpj);
    };

    /** Documento informado: a pessoa da ANTT, ou o que a GovPass sabe dele. */
    const preencher = () => {
      const documento = somenteDigitos(state.entity.cpfCnpj);
      if (
        state.entity.idPessoaAntt ||
        !isCpfCnpjValido(documento) ||
        documento === state.documentoPreenchido
      ) {
        return;
      }
      state.documentoPreenchido = documento;
      const { data, loading, preencherPessoaAntt } = usePessoaAnttService();
      state.loadings.push(loading);
      preencherPessoaAntt(documento, props.cliente?.idCliente ?? null)
        .then(() => {
          removeLoading(loading);
          const encontrada = data.value as PessoaAnttPayload | null;
          if (!encontrada) {
            state.entity.origem = null;
            return;
          }
          if (encontrada.origem === "ANT") {
            setEntity(encontrada);
            return;
          }
          state.entity.origem = encontrada.origem;
          if (!(state.entity.nome ?? "").trim()) {
            state.entity.nome = encontrada.nome ?? "";
          }
        })
        .catch(() => removeLoading(loading));
    };

    const save = () => {
      const service = usePessoaAnttService();
      state.loadings.push(service.loading);
      const payload: PessoaAnttPayload = {
        ...state.entity,
        cpfCnpj: somenteDigitos(state.entity.cpfCnpj),
        nome: (state.entity.nome ?? "").trim(),
        cliente: props.cliente?.idCliente
          ? ({ idCliente: props.cliente.idCliente } as ClientePayload)
          : state.entity.cliente ?? null,
        origem: null
      };
      (state.entity.idPessoaAntt
        ? service.updatePessoaAntt(state.entity.idPessoaAntt, payload)
        : service.saveNewPessoaAntt(payload)
      )
        .then(() => {
          removeLoading(service.loading);
          setEntity(service.data.value as PessoaAnttPayload);
          state.savedEntity = true;
          useToast().success("Cadastro da ANTT salvo.");
          closeScreen();
        })
        .catch(() => removeLoading(service.loading));
    };

    const submitSave = () => {
      if (!isCpfCnpjValido(state.entity.cpfCnpj)) {
        useToast().warning("Informe um CPF/CNPJ válido.");
        return;
      }
      if (!(state.entity.nome ?? "").trim()) {
        useToast().warning("Informe o nome ou a razão social.");
        return;
      }
      save();
    };

    onMounted(() => {
      const service = usePessoaAnttService();
      state.loadings.push(service.loading);
      (props.idPessoaAntt
        ? service.getPessoaAntt(props.idPessoaAntt)
        : service.newPessoaAntt(props.cliente?.idCliente ?? null)
      )
        .then(() => {
          removeLoading(service.loading);
          setEntity(service.data.value as PessoaAnttPayload);
          if (!props.idPessoaAntt && props.cpfCnpjInicial) {
            state.entity.cpfCnpj = formatCpfCnpj(props.cpfCnpjInicial);
            state.entity.nome = props.nomeInicial ?? "";
            preencher();
          }
          state.stateReady = true;
        })
        .catch(() => {
          removeLoading(service.loading);
          closeScreen();
        });
    });

    return {
      stateLoading,
      stateBlocked,
      titulo,
      cpfCnpjInvalido,
      avisoOrigem,
      preencher,
      submitSave,
      closeScreen,
      ...toRefs(state)
    };
  },
  props: {
    idPessoaAntt: Number,
    cliente: Object as PropType<ClientePayload>,
    /** Papel na tela que abriu: "Transportador", "Proprietário", "Arrendatário". */
    papel: String,
    cpfCnpjInicial: String,
    nomeInicial: String,
    parentLoading: Boolean
  },
  emits: ["after-register-screen-closed"]
});
</script>

<style scoped lang="scss">
.rntrc-aviso {
  padding: $space-3 $space-4;
  border-radius: $radius-md;
  border: 1px solid $border-color-light;
  font-size: $font-size-sm;

  &--info {
    background: $info-light;
    border-left: 4px solid $info;
  }
}
</style>
